import React, { useEffect, useMemo, useState } from "react";
import {
    Shield, ShieldCheck, UsersRound, UserPlus, Mail, KeyRound, X,
    Loader2, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SessionsPanel from "@/components/SessionsPanel";

const ROLES = ["admin", "engineer", "auditor", "viewer"];

/** Shared card shell — same visual system as the redesigned Portal pages
 * (icon-box header, rounded-xl border, subtle shadow). Defined locally
 * rather than imported from portal/PortalSettings.jsx since this page is
 * also used standalone at the main app's own /users route. */
export function Card({ icon: Icon, title, description, children, right = null }) {
    return (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-[0_1px_2px_rgba(16,24,40,0.02)]">
            <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    {Icon && (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EEF4FF] dark:bg-brand/10">
                            <Icon size={19} className="text-brand" />
                        </div>
                    )}
                    <div>
                        <h2 className="font-display text-[15px] font-bold">{title}</h2>
                        {description && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>}
                    </div>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

export default function Users() {
    const { user } = useAuth();
    const [tab, setTab] = useState("team"); // team | invites | sessions
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Access control
                </div>
                <h1 className="mt-1.5 font-display text-3xl font-bold tracking-tight">Team &amp; Roles</h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    RBAC hierarchy: <span className="font-mono">Admin → Engineer → Auditor → Viewer</span>. Only admins
                    can reassign roles or invite teammates.
                </p>
            </div>

            <div className="mb-5 flex flex-wrap gap-1 border-b border-border" data-testid="users-tabs">
                {[
                    { key: "team", label: "Team", show: true },
                    { key: "invites", label: "Pending invites", show: isAdmin },
                    { key: "sessions", label: "My sessions", show: true },
                ].filter((t) => t.show).map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`users-tab-${t.key}`}
                        className={[
                            "border-b-2 px-3.5 py-2.5 text-[13px] font-medium",
                            tab === t.key
                                ? "border-brand font-semibold text-brand"
                                : "border-transparent text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "team" && <TeamPanel currentUser={user} isAdmin={isAdmin} />}
            {tab === "invites" && isAdmin && <InvitesPanel />}
            {tab === "sessions" && <SessionsPanel />}
        </div>
    );
}

// ---------- Team panel (legacy users list) -----------------------------------

function TeamPanel({ currentUser, isAdmin }) {
    const [users, setUsers] = useState([]);
    const [err, setErr] = useState("");

    const refresh = async () => {
        try {
            const { data } = await http.get("/users");
            setUsers(data);
        } catch (e) {
            setErr(errMsg(e));
        }
    };

    useEffect(() => {
        refresh();
        // refresh is a stable closure intentionally fired once on
        // mount. Adding it to deps loops every render since the
        // function identity changes each time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeRole = async (id, role) => {
        try {
            await http.patch(`/users/${id}/role`, { role });
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                {[
                    ["Admin", "Full control — user management, agents, jobs, reports."],
                    ["Engineer", "Create / pause / resume / cancel jobs, register agents."],
                    ["Auditor", "Read-only access + user list + reports."],
                    ["Viewer", "Read-only dashboards and reports."],
                ].map(([r, d]) => (
                    <div key={r} className="rounded-lg border border-border bg-surface p-4">
                        <div className="flex items-center gap-2">
                            <Shield size={14} className="text-brand" />
                            <span className="font-display text-base font-bold">{r}</span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{d}</div>
                    </div>
                ))}
            </div>

            <Card icon={UsersRound} title="Team members" description={`${users.length} member${users.length === 1 ? "" : "s"} in this organization.`}>
                <div className="overflow-x-auto rounded-lg border border-border/60">
                    <table className="w-full text-left text-xs" data-testid="users-table">
                        <thead>
                            <tr className="bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                <th className="px-4 py-2">Name</th>
                                <th className="px-4 py-2">Email</th>
                                <th className="px-4 py-2">Role</th>
                                <th className="px-4 py-2">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t border-border/60 hover:bg-muted/20">
                                    <td className="px-4 py-2.5 font-semibold">{u.name}</td>
                                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{u.email}</td>
                                    <td className="px-4 py-2.5">
                                        {isAdmin ? (
                                            <select
                                                value={u.role}
                                                onChange={(e) => changeRole(u.id, e.target.value)}
                                                disabled={u.id === currentUser.id}
                                                data-testid={`user-role-select-${u.id}`}
                                                className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
                                            >
                                                {ROLES.map((r) => (
                                                    <option key={r} value={r}>
                                                        {r}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{u.role}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-muted-foreground">
                                        {(u.created_at || "").slice(0, 10)}
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                                        {err || "Loading users…"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

// ---------- Invites panel -----------------------------------------------------

function InvitesPanel() {
    const [invites, setInvites] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", role: "engineer", mode: "email" });
    const [submitting, setSubmitting] = useState(false);
    const [lastResult, setLastResult] = useState(null); // {temp_password|accept_url|...}

    const refresh = async () => {
        try {
            const { data } = await http.get("/users/invites");
            setInvites(data);
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    // Same fire-once-on-mount intent as the page-level loader. The
    // refresh closure changes identity each render; including it in
    // deps would loop. eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await http.post("/users/invites", form);
            setLastResult({ ...data, mode: form.mode });
            setForm({ email: "", name: "", role: "engineer", mode: form.mode });
            setShowForm(false);
            refresh();
            toast.success(form.mode === "manual"
                ? "User created — copy the temp password from the banner."
                : "Invite created.");
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    const cancel = async (id) => {
        if (!window.confirm("Cancel this invite?")) return;
        try {
            await http.delete(`/users/invites/${id}`);
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const pending = useMemo(() => invites.filter((i) => i.status === "pending"), [invites]);
    const completed = useMemo(() => invites.filter((i) => i.status !== "pending"), [invites]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                    {pending.length} pending · {completed.length} historical
                </div>
                <button
                    onClick={() => { setShowForm(!showForm); setLastResult(null); }}
                    data-testid="invite-toggle-form-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3.5 py-2 text-xs font-semibold text-white hover:opacity-90"
                >
                    <UserPlus size={14} /> {showForm ? "Cancel" : "Invite teammate"}
                </button>
            </div>

            {lastResult && <InviteResultBanner result={lastResult} onDismiss={() => setLastResult(null)} />}

            {showForm && (
                <form
                    onSubmit={submit}
                    data-testid="invite-form"
                    className="space-y-3 rounded-xl border border-border bg-surface p-5 shadow-[0_1px_2px_rgba(16,24,40,0.02)]"
                >
                    <div className="grid gap-3 md:grid-cols-2">
                        <input
                            required
                            type="email"
                            placeholder="teammate@company.com"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            data-testid="invite-email-input"
                            className="rounded border border-border bg-background px-3 py-2 text-sm"
                        />
                        <input
                            placeholder="Display name (optional)"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="invite-name-input"
                            className="rounded border border-border bg-background px-3 py-2 text-sm"
                        />
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Role
                            </label>
                            <select
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                data-testid="invite-role-select"
                                className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
                            >
                                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Delivery
                            </label>
                            <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup">
                                {[
                                    { v: "email", icon: Mail, label: "Email link" },
                                    { v: "manual", icon: KeyRound, label: "Temp password" },
                                ].map(({ v, icon: Icon, label }) => (
                                    <button
                                        type="button"
                                        key={v}
                                        onClick={() => setForm({ ...form, mode: v })}
                                        data-testid={`invite-mode-${v}`}
                                        className={[
                                            "inline-flex items-center justify-center gap-2 rounded border px-3 py-2 text-xs font-medium",
                                            form.mode === v
                                                ? "border-brand bg-brand/10 text-brand"
                                                : "border-border bg-background text-muted-foreground hover:text-foreground",
                                        ].join(" ")}
                                    >
                                        <Icon size={12} /> {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {form.mode === "email"
                            ? "Recipient gets an email with a one-time activation link. Falls back to a copy-paste URL if SMTP isn't configured."
                            : "Account is created immediately with a generated temp password. Forward it via Slack DM, password manager, etc. — recipient is forced to change it on first login."}
                    </p>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="invite-submit-btn"
                        className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                        {submitting ? <Loader2 size={14} className="mr-1.5 inline animate-spin" /> : null}
                        {form.mode === "manual" ? "Create user + temp password" : "Send invite"}
                    </button>
                </form>
            )}

            <Card icon={Mail} title="Invites" description="Pending and historical team invitations.">
                <div className="overflow-x-auto rounded-lg border border-border/60">
                    <table className="w-full text-left text-xs" data-testid="invites-table">
                        <thead>
                            <tr className="bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                <th className="px-4 py-2">Email</th>
                                <th className="px-4 py-2">Role</th>
                                <th className="px-4 py-2">Mode</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">Created</th>
                                <th className="px-4 py-2 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invites.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                                    No invites yet. Click "Invite teammate" to add one.
                                </td></tr>
                            )}
                            {invites.map((i) => (
                                <tr key={i.id} className="border-t border-border/60 hover:bg-muted/20">
                                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{i.email}</td>
                                    <td className="px-4 py-2.5">{i.role}</td>
                                    <td className="px-4 py-2.5">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                            {i.mode === "manual"
                                                ? <><KeyRound size={10} /> manual</>
                                                : <><Mail size={10} /> email</>}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <InviteStatusPill status={i.status} />
                                    </td>
                                    <td className="px-4 py-2.5 text-muted-foreground">
                                        {(i.created_at || "").slice(0, 10)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        {i.status === "pending" && (
                                            <button
                                                onClick={() => cancel(i.id)}
                                                data-testid={`invite-cancel-${i.id}`}
                                                aria-label={`Cancel invite for ${i.email}`}
                                                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:border-[hsl(var(--destructive))]/50 hover:text-[hsl(var(--destructive))]"
                                            >
                                                <X size={10} /> Cancel
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

function InviteStatusPill({ status }) {
    const cls = {
        pending: "border-amber-400/60 text-amber-300",
        accepted: "border-emerald-500/60 text-emerald-400",
        accepted_manual: "border-emerald-500/60 text-emerald-400",
        cancelled: "border-rose-400/40 text-rose-300",
        expired: "border-zinc-500/40 text-zinc-400",
    }[status] || "border-border text-muted-foreground";
    return (
        <span className={`mb-pill ${cls}`} data-testid={`invite-status-pill-${status}`}>
            {status.replace("_", " ")}
        </span>
    );
}

function InviteResultBanner({ result, onDismiss }) {
    const [copied, setCopied] = useState(false);
    const value = result.mode === "manual" ? result.temp_password : result.accept_url;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Clipboard unavailable — copy manually");
        }
    };
    return (
        <div
            data-testid="invite-result-banner"
            className="flex items-start gap-3 rounded-lg border border-amber-400/40 bg-amber-500/[0.06] p-4"
        >
            <KeyRound size={16} className="mt-0.5 text-amber-300" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-100">
                    {result.mode === "manual"
                        ? "Forward this temp password to the user — it won't be shown again."
                        : (result.email_sent
                            ? "Invite email sent. The accept link is also copy-paste-able below."
                            : "SMTP isn't configured — copy this link and send it manually.")}
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <code
                        data-testid="invite-result-value"
                        className="flex-1 truncate rounded bg-black/30 px-2 py-1.5 font-mono text-xs text-amber-100"
                    >
                        {value}
                    </code>
                    <button
                        onClick={copy}
                        data-testid="invite-result-copy-btn"
                        className="inline-flex items-center gap-1 rounded border border-amber-400/40 px-2 py-1 text-[11px] text-amber-200 hover:bg-amber-500/10"
                    >
                        {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy"}
                    </button>
                </div>
            </div>
            <button
                onClick={onDismiss}
                data-testid="invite-result-dismiss-btn"
                className="text-amber-300 hover:text-amber-100"
                aria-label="Dismiss"
            >
                <X size={14} />
            </button>
        </div>
    );
}

